import { useRef, useState, type FormEvent } from 'react';
import { Plus, Search, Upload } from 'lucide-react';
import { Hero, Kpis } from './Promo';
import { dateLabel } from '@ck/sdk';
import { api } from './api';
import { Copy, Empty, Failed, Loading, Modal, Note, PageHead, useAction, useApi } from './ui';

/** Reads a CSV file into rows keyed by the header line. Handles quoted fields and ; or , separators. */
export function parseCsv(text: string): Record<string, string>[] {
  const first = text.split('\n')[0] ?? '';
  const delim = first.split(';').length > first.split(',').length ? ';' : ',';
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch; continue; }
    if (ch === '"') q = true;
    else if (ch === delim) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i++; row.push(cell); cell = ''; if (row.some((c) => c.trim())) rows.push(row); row = []; }
    else cell += ch;
  }
  row.push(cell); if (row.some((c) => c.trim())) rows.push(row);
  const [head, ...body] = rows;
  return head ? body.map((r) => Object.fromEntries(head.map((h, i) => [h.trim(), (r[i] ?? '').trim()]))) : [];
}

interface Lead { id: string; name: string; email: string; phone: string | null; country: string | null; source: string | null; trader_id: string | null; created_at: string }

/** Contacts the affiliate brings in by hand or from a file. They become referrals when they sign up. */
export function MyClients() {
  const d = useApi(() => api.get<{ leads: Lead[] }>('/v1/partner/leads').then((r) => r.leads));
  const [adding, setAdding] = useState(false);
  const a = useAction();
  const file = useRef<HTMLInputElement>(null);
  async function importFile(f: File) {
    const rows = parseCsv(await f.text());
    if (!rows.length) { a.setError('That file has no rows. The first line should name the columns: name, email, phone, country.'); return; }
    const r = await a.run(() => api.post<{ added: number; skipped: number }>('/v1/partner/leads/import', { rows: rows.slice(0, 5000) }));
    if (r) { a.setDone(`Added ${r.added} contact${r.added === 1 ? '' : 's'}${r.skipped ? `. Skipped ${r.skipped}: missing email, already a client, or already someone’s contact.` : '.'}`); d.reload(); }
    if (file.current) file.current.value = '';
  }
  const list = d.data ?? [];
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState<'all' | 'joined' | 'invited'>('all');
  const joined = list.filter((l) => l.trader_id).length;
  const shown = list.filter((l) => (seg === 'all' || (seg === 'joined') === !!l.trader_id) && (!q || `${l.name} ${l.email} ${l.phone ?? ''}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <Hero kicker="My clients" title={`${list.length} client${list.length === 1 ? '' : 's'}`} lead="Add the traders you work with, one by one or from a spreadsheet. When they sign up with the same email, they count as your referral even if they didn’t use your link."
        side={<div className="ibx-hero-a"><button className="ibx-btn" onClick={() => setAdding(true)}><Plus />Add client</button><button className="ibx-ghost" onClick={() => file.current?.click()}><Upload />Import CSV</button><input ref={file} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); }} /></div>} />
      <Kpis items={[['All clients', list.length], ['Signed up', joined], ['Invited', list.length - joined], ['Joined rate', list.length ? `${Math.round((joined / list.length) * 100)}%` : '—']]} />
      {a.error && <Note kind="err">{a.error}</Note>}{a.done && <Note>{a.done}</Note>}
      <section className="ibx-card">
        <div className="ibx-bar">
          <div className="ibx-seg">{([['all', 'All'], ['joined', 'Signed up'], ['invited', 'Invited']] as const).map(([k, l]) => <button key={k} className={seg === k ? 'on' : ''} onClick={() => setSeg(k)}>{l}</button>)}</div>
          <label className="ibx-search"><Search /><input placeholder="Search name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        </div>
        {d.error ? <Failed error={d.error} retry={d.reload} /> : !d.data ? <Loading /> : shown.length === 0 ? <Empty title={list.length ? 'Nothing matches.' : 'No clients added yet.'}>{list.length ? 'Try another filter.' : 'Add one, or import a CSV with columns name, email, phone, country.'}</Empty> : (
          <div className="ibx-rows">{shown.map((l) => (
            <div key={l.id} className="ibx-row"><span className="ibx-av">{l.name.slice(0, 1).toUpperCase()}</span>
              <div><b>{l.name}</b><small>{l.email}{l.phone ? ` · ${l.phone}` : ''}{l.country ? ` · ${l.country}` : ''}</small></div>
              <div className="r">{l.trader_id ? <span className="badge s-approved">Signed up</span> : <span className="badge s-pending">Invited</span>}<small>Added {dateLabel(l.created_at)}</small></div></div>
          ))}</div>
        )}
      </section>
      {adding && <AddClient onClose={(ok) => { setAdding(false); if (ok) d.reload(); }} />}
    </>
  );
}

function AddClient({ onClose }: { onClose: (ok: boolean) => void }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', country: '', notes: '' });
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Add a client" onClose={() => onClose(false)}>
      <form className="form" onSubmit={async (e: FormEvent) => { e.preventDefault(); if (await a.run(() => api.post('/v1/partner/leads', f))) onClose(true); }}>
        <div className="row2"><label>Name<input required value={f.name} onChange={set('name')} /></label><label>Email<input type="email" required value={f.email} onChange={set('email')} /></label></div>
        <div className="row2"><label>Phone<input value={f.phone} onChange={set('phone')} /></label><label>Country<input value={f.country} onChange={set('country')} /></label></div>
        <label>Notes<textarea rows={3} value={f.notes} onChange={set('notes')} /></label>
        {a.error && <Note kind="err">{a.error}</Note>}
        <div className="row-a"><button type="button" className="link" onClick={() => onClose(false)}>Cancel</button><button className="btn" disabled={a.busy}>Add client</button></div>
      </form>
    </Modal>
  );
}

/** Change the link name, e.g. ?ref=AMANI-FX. Old links keep working. */
export function CustomLink({ code, clientUrl, onChanged }: { code: string; clientUrl: string; onChanged: () => void }) {
  const [v, setV] = useState(code);
  const a = useAction();
  return (
    <section className="panel form">
      <h3>Your link name</h3>
      <p className="fine">Make your link easy to remember. Links you already shared keep working.</p>
      <form className="inline-form" onSubmit={async (e) => { e.preventDefault(); if (await a.run(() => api.post('/v1/partner/code', { code: v }), 'Saved. Your new link is ready.')) onChanged(); }}>
        <label className="grow">Link name<input value={v} onChange={(e) => setV(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} maxLength={24} /></label>
        <button className="btn" disabled={a.busy || v === code || v.length < 3}>Save</button>
      </form>
      <div className="reflink"><span className="mono">{clientUrl}/?ref={v || code}</span><Copy text={`${clientUrl}/?ref=${v || code}`} label="Copy link" /></div>
      {a.error && <Note kind="err">{a.error}</Note>}{a.done && <Note>{a.done}</Note>}
    </section>
  );
}
